import app from '@frontastic/catwalk/src/js/app/app';
import TagManager from '../../../../classes/TagManager';
import { useCloseOnEsc } from '../../../../hooks/useCloseOnEsc';
import { useOutsideClick } from '../../../../hooks/useOutsideClick';
import mergeClasses from '../../../../utils/shallowMerge';
import Button from '../../../atoms/button';
import Notification from '../../../atoms/notification';
import PrimaryLoader from '../../../atoms/primaryLoader/PrimaryLoader';
import { Heading } from '../../../typography/heading/Heading';
import { Text } from '../../../typography/text/Text';
import { loginSchema } from '../utils/validationSchemas';
import { useCustomScrollToTop } from '../../../../utils/useCustomScrollToTop';
import defaultClasses from './Login.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useMedia } from 'react-use';
import { useSelector } from 'react-redux';
import { useCartContext } from '../../../../provider/cart/CartContext';
import { formatErrorMessage } from './utils';
import { useWishlistContext } from '../../../../provider/wishlist/WishlistContext';
import { useNostoContext } from '../../../../provider/nosto/NostoContext';

import { useCsrfToken } from '../../../../utils/useCsrfToken';
import PasswordField from '../../../atoms/passwordField';
import EmailField from '../../../atoms/emailField';
import { useYupValidationResolver } from '../../../../utils/useYupValidationResolver';
import { useRecaptcha } from '@srcDS/hooks/useRecaptcha';

const Login = ({
  onClose = () => null,
  onLogin = () => null,
  customClasses,
  goToRegistrationAction = () => null,
  contentBlock,
  promo,
  redirectUrl,
  scrollToTop,
}) => {
  const isDesktop = useMedia('(min-width: 1025px)', true);
  const styles = mergeClasses(defaultClasses, customClasses);
  const defaultEmail = sessionStorage.getItem('newsLetterEmail') || null;
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { nextWishlistAction, resetNextWishlistAction } = useWishlistContext();
  const { setLoading: setCartLoading } = useCartContext();
  const [emailIsReady, setEmailIsReady] = useState(false);
  const { omneo } = useSelector((state) => state['demo-common']);
  const { client } = omneo;
  const { nostoUpdateCustomer } = useNostoContext();

  const { executeRecaptcha, refetchRecaptcha } = useRecaptcha();
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const resolver = useYupValidationResolver(loginSchema);

  const { register, handleSubmit, formState, setValue, getValues } = useForm({
    resolver,
    mode: 'all',
  });

  const { errors } = formState;

  const handleReCaptchaVerify = React.useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }
    const recTokenResult = await executeRecaptcha('login_account_form');
    setRecaptchaToken(recTokenResult);
    setValue('recaptcha_token', recTokenResult, { shouldDirty: false });
  }, [executeRecaptcha]);

  useEffect(() => {
    setRecaptchaToken(null);
    setValue('recaptcha_token', null);
    setError(null);
    handleReCaptchaVerify();
  }, [executeRecaptcha]);

  const { csrfTokenComponent } = useCsrfToken('login', register, setValue);
  const loginContainerRef = useRef(null);

  const { goToTop } = useCustomScrollToTop();

  useEffect(() => {
    scrollToTop && goToTop();
  }, []);

  useCloseOnEsc({ onClose, activate: isDesktop });

  useOutsideClick({
    ref: loginContainerRef,
    handler: onClose,
    activate: isDesktop,
  });

  const handleError = async (error) => {
    setRecaptchaToken(null);
    setError(error);
    await handleReCaptchaVerify();
    setLoading(false);
  };

  const onSubmit = async ({
    email,
    password,
    csrf_token,
    recaptcha_token: recaptchaToken,
    email_validation_code,
    email_validation_response,
  }) => {
    if (!email || !password) return;

    if (loading) {
      return;
    }

    setLoading(true);

    let recaptcha_token = recaptchaToken;
    if (!recaptcha_token) {
      recaptcha_token = await refetchRecaptcha();
      if (!recaptcha_token) {
        setLoading(false);
        console.error('Failed to get reCAPTCHA token');
        return;
      }
    }

    await onLogin({
      email,
      password,
      csrf_token,
      recaptcha_token,
      errorCallback: handleError,
      email_validation_code,
      email_validation_response,
      successCallback: async (data) => {
        if (data?.loggedIn) {
          if (client && client.ready) {
            client.get('profile').then((response) => {
              const comms = response?.body?.data?.attributes?.comms || [];
              nostoUpdateCustomer({
                marketingComms: comms?.email_optout ? false : true,
              });
            });
          }

          TagManager.loginEvent(data);

          if (nextWishlistAction && nextWishlistAction.action === 'addAfterSigning') {
            setCartLoading(true);
            const { method, item, lineItemId } = nextWishlistAction;
            await method(item, lineItemId, () => {
              resetNextWishlistAction();
              setCartLoading(false);
            });
          }
          onClose();
          if (redirectUrl) {
            app.getRouter().replace('Frontastic.Frontend.Master.Account.profile');
          }
        }
      },
    }).then(() => {});
  };

  return (
    <div ref={loginContainerRef} className={clsx(styles.loginContainer, styles.container)}>
      <div className={styles.headingWrapper}>
        <Heading textTransform="firstletter" className={styles.heading} as="h4">
          Login
        </Heading>

        {contentBlock && (
          <div className={styles.contentBlockTextWrapper}>
            <Text as="p" align="left" size="xs" weight="light" className={styles.contentBlockText}>
              {contentBlock}
            </Text>
          </div>
        )}
      </div>
      <div id="signInError" className={styles.error}>
        <Text size="xs" weight="light">
          You must be signed in to add items to your wishlist
        </Text>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {csrfTokenComponent}

        <input type="hidden" {...register('recaptcha_token', { value: recaptchaToken })} />
        <EmailField
          id="1"
          className={clsx(styles.textField, styles.textFieldCommon)}
          placeholder="Email Address"
          variant="underlined"
          name="email"
          isPureField
          register={register}
          setError={() => {}}
          setValue={setValue}
          defaultValue={defaultEmail || getValues('email')}
          setEmailIsReady={setEmailIsReady}
          errorMessage={errors?.email?.message}
          error={!!errors?.email}
        />
        <PasswordField
          onlyField
          hideNote
          placeholder="Password"
          variant="underlined"
          name="password"
          register={register}
          errorMessage={!!errors?.password?.message}
          error={!!errors?.password}
          inputStyle={clsx(styles.passTextfield, styles.textFieldCommon)}
        />
        <Link
          className={styles.linkSetPassword}
          to="/forgot-password-page"
          onClick={() => onClose()}
          aria-label="Reset Password"
        >
          Reset Password
        </Link>
        {error && (
          <div className={styles.loginErrors}>
            <Notification type="error" message={formatErrorMessage(error)} />
          </div>
        )}
        <Button
          disabled={!formState.isDirty || !formState.isValid || (getValues('email') && !emailIsReady) || loading}
          className={clsx(styles.btnLogin, styles.btnLoginDefault)}
          type="submit"
          size="large"
        >
          {loading || (getValues('email') && !emailIsReady) ? (
            <PrimaryLoader mode="segment" theme="light" size={20} />
          ) : (
            'Login to account'
          )}
        </Button>
      </form>
      <div className={styles.createAccountContainer}>
        <Text className={clsx(styles.text, styles.dontHaveText)} align="center" as="p">
          Don&#39;t have an account?
        </Text>
        <Button
          size="large"
          className={clsx(styles.btnAll, styles.btnAllDefault)}
          variant="secondary"
          type="button"
          onClick={goToRegistrationAction}
        >
          create an account for vip
        </Button>

        {promo && (
          <Text align="center" as="p" className={styles.vipText}>
            {promo}
          </Text>
        )}
      </div>
    </div>
  );
};

Login.propTypes = {
  onClose: PropTypes.func,
  onLogin: PropTypes.func,
  goToRegistrationAction: PropTypes.func,
  errorsFromBack: PropTypes.string,
  defaultValues: PropTypes.shape({
    email: PropTypes.string,
    password: PropTypes.string,
  }),
};

export default Login;
